// Componentes e utilitários compartilhados pelas telas de Gestão de Estoque. Telas
// operacionais (vistoria, contagem, saídas) precisam funcionar no celular — por isso o
// useIsMobile e os alvos de toque maiores (min 44px) nos inputs/botões.
import { useEffect, useState } from "react";
import { X } from "lucide-react";

export const palette = (isLight) => ({
  bg: isLight ? "#F8F7FC" : "#0F0A1E",
  card: isLight ? "#FFFFFF" : "#1A1030",
  border: isLight ? "#E5E0F0" : "#2E2050",
  bgActive: isLight ? "#EEF2FF" : "#241A3D",
  text: isLight ? "#1A1030" : "#EDE9FA",
  muted: isLight ? "#7C6FA0" : "#9585C0",
  brand: "#7C3AED",
  success: "#10B981",
  warning: "#F59E0B",
  danger: "#EF4444",
  rowAlt: isLight ? "#FAF8FE" : "#1E1435",
});

export function useIsMobile(breakpoint = 768) {
  const query = `(max-width: ${breakpoint}px)`;
  const [isMobile, setIsMobile] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = (e) => setIsMobile(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);
  return isMobile;
}

export function inputStyle(C) {
  return {
    background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8,
    padding: "10px 12px", fontSize: 14, color: C.text, fontFamily: "inherit", outline: "none",
    width: "100%", minHeight: 42, boxSizing: "border-box",
  };
}

export function btnStyle(color, secondary = false) {
  return {
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
    background: secondary ? "transparent" : color,
    border: secondary ? `1px solid ${color}` : "none",
    color: secondary ? color : "#fff",
    borderRadius: 8, padding: "10px 16px", fontSize: 13, fontWeight: 700, minHeight: 42,
    fontFamily: "inherit", cursor: "pointer", whiteSpace: "nowrap",
  };
}

export function Campo({ label, children, C, obrigatorio = false, ajuda }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
      <label style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted }}>
        {label}{obrigatorio && <span style={{ color: C.danger }}> *</span>}
      </label>
      {children}
      {ajuda && <span style={{ fontSize: 11, color: C.muted }}>{ajuda}</span>}
    </div>
  );
}

// ─── Números ──────────────────────────────────────────────────────────────────
// Quantidade/valor trafegam como STRING decimal com ponto ("12.5") até o backend, que calcula
// em Decimal — nunca passam por float no caminho de gravação.

/** "1.234,56" | "1234,56" | "1234.56" → "1234.56"; vazio → null. */
export function parseNumero(texto) {
  if (texto === null || texto === undefined) return null;
  const s = String(texto).trim();
  if (!s) return null;
  const normalizado = s.includes(",") ? s.replace(/\./g, "").replace(",", ".") : s;
  return /^\d+(\.\d+)?$/.test(normalizado) ? normalizado : null;
}

/** Número vindo da API → texto editável no padrão brasileiro ("12,5"). */
export function numeroParaInput(valor) {
  if (valor === null || valor === undefined || valor === "") return "";
  return String(valor).replace(".", ",");
}

/**
 * Campo numérico que NÃO aceita texto: só dígitos e um separador decimal (vírgula ou ponto),
 * com no máximo `casas` casas decimais. value/onChange trabalham com o texto digitado.
 */
export function NumeroInput({ value, onChange, casas = 4, C, style, ...rest }) {
  function handleChange(e) {
    const bruto = e.target.value.replace(/\s/g, "");
    if (bruto === "") return onChange("");
    const re = casas > 0 ? new RegExp(`^\\d*([.,]\\d{0,${casas}})?$`) : /^\d*$/;
    if (re.test(bruto)) onChange(bruto);
  }
  return (
    <input
      type="text"
      inputMode={casas > 0 ? "decimal" : "numeric"}
      autoComplete="off"
      value={value ?? ""}
      onChange={handleChange}
      style={{ ...inputStyle(C), textAlign: "right", fontVariantNumeric: "tabular-nums", ...style }}
      {...rest}
    />
  );
}

const fmtQtd = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 4 });
const fmtBRL = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });

export const formatQtd = (v) => (v === null || v === undefined ? "—" : fmtQtd.format(Number(v)));
export const formatBRL = (v) => (v === null || v === undefined ? "—" : fmtBRL.format(Number(v)));

// ─── Blocos visuais ─────────────────────────────────────────────────────────

export function AvisoErro({ C, children, onClose }) {
  if (!children) return null;
  return (
    <div role="alert" style={{ display: "flex", gap: 8, alignItems: "flex-start", background: `${C.danger}18`, border: `1px solid ${C.danger}`, color: C.danger, borderRadius: 8, padding: "8px 12px", fontSize: 13, marginBottom: 12 }}>
      <span style={{ flex: 1 }}>{children}</span>
      {onClose && (
        <button onClick={onClose} aria-label="Fechar aviso" style={{ background: "none", border: "none", color: C.danger, cursor: "pointer", display: "flex", padding: 0 }}>
          <X size={16} />
        </button>
      )}
    </div>
  );
}

export function Modal({ C, titulo, onClose, children, rodape, largura = 520 }) {
  const isMobile = useIsMobile();
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", display: "flex", alignItems: isMobile ? "flex-end" : "center", justifyContent: "center", zIndex: 60, padding: isMobile ? 0 : 16 }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: isMobile ? "16px 16px 0 0" : 16, width: "100%", maxWidth: isMobile ? "100%" : largura, boxShadow: "0 24px 64px rgba(0,0,0,0.4)", maxHeight: isMobile ? "92vh" : "90vh", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 20px 12px" }}>
          <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>{titulo}</p>
          <button onClick={onClose} aria-label="Fechar" style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, display: "flex", padding: 4 }}>
            <X size={20} />
          </button>
        </div>
        <div style={{ padding: "0 20px 16px", overflowY: "auto" }}>{children}</div>
        {rodape && (
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "12px 20px 18px", borderTop: `1px solid ${C.border}` }}>
            {rodape}
          </div>
        )}
      </div>
    </div>
  );
}

export function BadgeAtivo({ ativo, C }) {
  return (
    <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: ativo ? `${C.success}1F` : `${C.muted}1F`, color: ativo ? C.success : C.muted }}>
      {ativo ? "Ativo" : "Inativo"}
    </span>
  );
}
